import { RefObject, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFeatureAccessMessageType } from '../../../../common/iam/access/feature-access';
import { FeatureAccessMessageType, FeatureId } from '../../../../common/iam/access/feature-access/types';
import { UserPrivate } from '../../../../common/iam/users/types';
import {
  isClientOrTeamOrFreeTrialUser,
  isCompanyStandardLicenseUser,
  isCompanyUser,
  isDmaFreemiumUser,
  isInvestorUser,
  isManualTestUser,
  isSelfSignupCompanyUser,
  isStandardOrExtendedCoverageUser,
  isTeamUser,
  userIsMemberOf,
} from '../../../../common/utils';
import { StateTree } from '../../../untidy/redux/reducers';
import { getChatInternalMode, getViewSetting, loggedInUser } from '../../../untidy/redux/selectors';
import { getTeamSession, getTeamBrowser } from '../utilities/storage';
import { ViewSettingKey } from '../view-settings/registry';
import { SpecialUserGroupId } from '@upright/common/iam/user-groups/types';
import { UI_ROOT_ELEMENT_ID } from '@upright/ui/common/constants';

export function useGetLoggedInUser(): UserPrivate | undefined {
  return useSelector(loggedInUser);
}

export function useIsTeam(): boolean {
  const user = useSelector(loggedInUser);
  return isTeamUser(user);
}

/**
 * Whether the value-set selector (impact-category weights) is offered at all. Currently: nobody.
 *
 * The feature cannot keep a promise to anyone. The chosen value set lives in Redux alone, so it
 * resets to Equal weights on every reload and never survives to the next visit — a control that
 * undoes itself is worse than no control, and the usage bore that out (92 external users in a
 * year, 33 of whom changed anything). Retired for team as well as customers (PLT-6740).
 *
 * This is the single switch behind every value-set surface: the sidebar mount, the internal-tools
 * menu row, the dark header's user menu, and the two "Value set: X" captions on the product view.
 * Nothing about the feature is deleted, so bringing it back is an edit to this one function —
 * `useIsTeam()` for team only, `true` for everyone. Offering it to customers should wait until the
 * selection is persisted (`user.values` is the natural home and is currently unused), or it will
 * still undo itself on every reload.
 */
export function useValueSetSelectorAvailable(): boolean {
  return false;
}

// OA: This function is not a hook, so it's kinda in a funny place,
// but it still probably makes sense to keep it here next to its friends.
export function isOrWasTeam(user: UserPrivate | undefined) {
  // Not logged in, but browser has been used by team user
  if (getTeamSession() || getTeamBrowser()) {
    return true;
  }
  // Not logged in, and browser has no trace of team usage
  if (!user) {
    return false;
  }
  // Logged in as either team or test user
  return isTeamUser(user) || isManualTestUser(user);
}

/**
 * Read a single per-feature view setting (PLT-4824) with the team-user gate
 * applied at the hook layer. Returns `true` only when BOTH the toggle is on
 * AND the current user is a team user — a stale localStorage flag on a
 * non-team account is a no-op.
 *
 * Use this from any UI surface that the View settings page controls.
 * Don't use the raw `getViewSetting` selector for those surfaces, or the
 * team-only invariant will silently degrade.
 */
export function useViewSetting(key: ViewSettingKey): boolean {
  const user = useSelector(loggedInUser);
  const enabled = useSelector((state: StateTree) => getViewSetting(state, key));
  return enabled && isTeamUser(user);
}

/**
 * Effective state of the chat internal-mode toggle (PLT-4475).
 *
 * Returns `true` only when BOTH the toggle is on AND the current user is a
 * team user. Re-checking team status here (rather than just trusting the
 * Redux flag) is the defence-in-depth invariant from the design spec: a
 * stale localStorage value on a non-team account should be a no-op.
 *
 * Use this from UI surfaces that the panel controls — the toggle render in
 * the composer, the debug panel mount, and any future internal-only chat
 * affordances. Don't use the raw `getChatInternalMode` selector for those
 * surfaces, or the team-only invariant will silently degrade.
 */
export function useIsChatInternalMode(): boolean {
  const user = useSelector(loggedInUser);
  const enabled = useSelector(getChatInternalMode);
  return enabled && isTeamUser(user);
}

export function useIsClientOrTeam(): boolean {
  const user = useSelector(loggedInUser);
  return isTeamUser(user) || userIsMemberOf(SpecialUserGroupId.CLIENTS, user);
}

export function useIsClientOrTeamOrFreeTrial(): boolean {
  const user = useSelector(loggedInUser);
  return isClientOrTeamOrFreeTrialUser(user);
}

export function useIsClientOrFreeTrial(): boolean {
  const user = useSelector(loggedInUser);
  return userIsMemberOf(SpecialUserGroupId.CLIENTS_AND_FREE_TRIAL_USERS, user);
}

export function useIsInvestorUser(): boolean {
  const user = useSelector(loggedInUser);
  return isInvestorUser(user);
}

export function useIsSelfSignupCompanyUser(): boolean {
  const user = useSelector(loggedInUser);
  return isSelfSignupCompanyUser(user);
}

export function useIsCompanyUser(): boolean {
  const user = useSelector(loggedInUser);
  return isCompanyUser(user);
}

export function useIsCompanyStandardLicenseUser(): boolean {
  const user = useSelector(loggedInUser);
  return isCompanyStandardLicenseUser(user);
}

export function useIsStandardOrExtendedCoverageUser() {
  const user = useSelector(loggedInUser);
  return isStandardOrExtendedCoverageUser(user);
}

export function useIsDMAFreemiumUser(): boolean {
  const user = useSelector(loggedInUser);
  return isDmaFreemiumUser(user);
}

export function useGetFeatureAccessMessageType(
  featureId: FeatureId,
  entityId?: string,
): FeatureAccessMessageType | undefined {
  const user = useSelector(loggedInUser);
  return getFeatureAccessMessageType(featureId, user, entityId);
}

export function useHasAccessToFeature(featureId: FeatureId): boolean {
  return useGetFeatureAccessMessageType(featureId) === FeatureAccessMessageType.AVAILABLE;
}

export function useFeatureIsAvailableToUser(featureId: FeatureId, entityId?: string) {
  const accessMessageType = useGetFeatureAccessMessageType(featureId, entityId);
  return accessMessageType === FeatureAccessMessageType.AVAILABLE;
}

/*
 * Overlays — react-aria modals and popovers, and our own `createPortal` layers —
 * are mounted on `document.body`, outside the app root that renders the rest of
 * the page, so their DOM lives outside the React subtree that opened them.
 * Returns the `document.body` child that such a node sits under, or null for
 * nodes in the normal document flow.
 *
 * Landing outside the app root is the stable signal here: the portal DOM shape
 * differs per overlay component (a react-aria popover gets an extra wrapper that
 * a modal does not) and is free to change between library versions.
 */
function portalledOverlayRoot(node: Node): Element | null {
  const start = node instanceof Element ? node : node.parentElement;
  const appRoot = document.getElementById(UI_ROOT_ELEMENT_ID);
  if (start === null || appRoot === null || appRoot.contains(start)) {
    return null;
  }
  let root: Element = start;
  while (root.parentElement !== null && root.parentElement !== document.body) {
    root = root.parentElement;
  }
  return root.parentElement === document.body ? root : null;
}

/*
 * Can be used for components needing some function to happen when clicking outside of it, e.g. closing tooltips and dropdowns.
 * Pass an array with one or more Refs to check whether the click happens outside of all of them.
 *
 * A click inside an overlay layered above the watched elements does not count as
 * an outside click. Such an overlay is normally opened from within the watched
 * element, so dismissing that element would unmount the overlay the user is
 * interacting with (PLT-5879). Overlays that *contain* a watched element are
 * unaffected, so a dropdown inside a dialog still closes when the rest of that
 * dialog is clicked.
 */
export function useOnClickOutside(refs: Array<RefObject<HTMLElement>>, handler: (event: Event) => void) {
  useEffect(() => {
    const listener = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      const overlay = portalledOverlayRoot(target);
      if (overlay !== null && refs.every(ref => ref.current !== null && !overlay.contains(ref.current))) {
        return;
      }
      const allRefsExistAndClickNotInThem = refs.every(ref => {
        return !!ref.current && !ref.current.contains(target);
      });
      if (allRefsExistAndClickNotInThem) {
        handler(event);
      }
    };

    document.addEventListener('click', listener, { capture: true });
    document.addEventListener('touchstart', listener, { capture: true });

    return () => {
      document.removeEventListener('click', listener, { capture: true });
      document.removeEventListener('touchstart', listener, { capture: true });
    };
  }, [refs, handler]);
}

export const useIsMounted = () => {
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return () => isMounted.current;
};

/**
 * Can be used to handle stuff that class components did with prevProps/prevState
 */
export function usePrevious<T>(value: T) {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

/**
 * The width in whole pixels of the element a ref points at, kept current as it resizes. Zero until
 * the first measurement, and for a ref that is empty or absent — the ref is only ever read, so an
 * optional one is allowed rather than making every caller branch around the hook.
 */
export const useRefWidth = (ref: RefObject<HTMLElement | null> | undefined) => {
  const [refWidth, setRefWidth] = useState(0);
  useEffect(() => {
    const updateRefWidth = () => {
      if (ref?.current) {
        setRefWidth(ref.current.offsetWidth);
      }
    };

    updateRefWidth();

    const resizeObserver = new ResizeObserver(updateRefWidth);
    if (ref?.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [refWidth, ref]);

  return refWidth;
};
